import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { announcements as announcementsApi, calls as callsApi, orders as ordersApi } from "../api/endpoints";
import { EmptyState, MetricStrip, PageHeader, SectionCard } from "../components/Workspace";
import type { Announcement, User } from "../types";

interface Props {
  user: User;
}

export default function DashboardPage({ user }: Props) {
  const [stats, setStats] = useState({
    totalCalls: 0,
    pendingCalls: 0,
    completedCalls: 0,
    totalOrders: 0,
    overdueCalls: 0,
  });
  const [items, setItems] = useState<Announcement[]>([]);
  const userRole = user.role?.name || "";
  const isAdmin = ["SuperAdmin", "Admin"].includes(userRole);
  const isGuest = userRole === "Guest";
  const isShopper = userRole === "Mystery Shopper";
  const isRequestor = !isAdmin && !isGuest && !isShopper;

  useEffect(() => {
    let cancelled = false;

    const loadDashboard = async () => {
      try {
        const statuses = await callsApi.statuses();
        const completedStatusId = statuses.find((status) => status.name === "Completed")?.id;

        const results = await Promise.allSettled([
          callsApi.list("limit=1&has_scheduled=true"),
          completedStatusId ? callsApi.list(`limit=1&status_id=${completedStatusId}`) : Promise.resolve({ count: 0 }),
          callsApi.list("limit=1"),
          ordersApi.list("limit=1"),
          callsApi.list("limit=1&is_overdue=true"),
          announcementsApi.list(),
        ]);

        if (cancelled) return;

        const [
          pendingResult,
          completedResult,
          allResult,
          allOrdersResult,
          overdueResult,
          announcementsResult,
        ] = results;

        const settledValue = <T,>(result: PromiseSettledResult<T>, fallback: T): T =>
          result.status === "fulfilled" ? result.value : fallback;

        const emptyPage = { items: [], count: 0 };

        const pending = settledValue(pendingResult, emptyPage);
        const completed = settledValue(completedResult, emptyPage);
        const all = settledValue(allResult, emptyPage);
        const allOrders = settledValue(allOrdersResult, emptyPage);
        const overdue = settledValue(overdueResult, emptyPage);
        const anns = settledValue(announcementsResult, [] as Announcement[]);

        setStats({
          totalCalls: (all as any).count || 0,
          pendingCalls: (pending as any).count || 0,
          completedCalls: (completed as any).count || 0,
          totalOrders: (allOrders as any).count || 0,
          overdueCalls: (overdue as any).count || 0,
        });
        setItems(anns);
      } catch {
        if (!cancelled) {
          setItems([]);
        }
      }
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, []);

  const quickLinks = isAdmin
    ? [
        { title: "Live Calls Desk", to: "/calls", copy: "Move from queue health into active call management." },
        { title: "Orders", to: "/orders", copy: "Check invoice status, payment flow, and requestor demand." },
        { title: "Purchase Calls", to: "/purchase", copy: "Start new onboarding without leaving the operations workspace." },
        { title: "Reports", to: "/reports", copy: "Switch into exports, billing, and management reporting." },
      ]
    : [
        { title: "My Calls", to: "/calls", copy: "Review your active calls and move directly into detail and survey work." },
        ...(isRequestor
          ? [
              { title: "My Orders", to: "/orders", copy: "Track invoice IDs, PO numbers, and order state." },
              { title: "Reports", to: "/reports", copy: "Open reporting and export views for your portfolio." },
            ]
          : []),
        { title: "My Profile", to: "/profile", copy: "Keep your account details and preferences current." },
      ];

  const metricItems = isAdmin
    ? [
        { label: "Pending queue", value: stats.pendingCalls, detail: "Calls currently waiting on action", tone: "warning" as const },
        { label: "Overdue follow-up", value: stats.overdueCalls, detail: "High-risk calls that need direct attention", tone: "danger" as const },
        { label: "Completed", value: stats.completedCalls, detail: "Calls already moved through the workflow", tone: "success" as const },
        { label: "Orders in system", value: stats.totalOrders, detail: "Current purchasing and invoice volume", tone: "brand" as const },
      ]
    : [
        { label: "Visible calls", value: stats.totalCalls, detail: "Calls in your current workspace", tone: "brand" as const },
        { label: "Pending work", value: stats.pendingCalls, detail: "Calls still waiting to be completed", tone: "warning" as const },
        { label: "Completed", value: stats.completedCalls, detail: "Calls moved through the process", tone: "success" as const },
        { label: "Announcements", value: items.length, detail: "Current internal notices", tone: "neutral" as const },
      ];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={isAdmin ? "Operations overview" : "Workspace overview"}
        title={`Today, ${user.first_name}`}
        description={
          isAdmin
            ? "Use this overview to move directly into queue management, overdue follow-up, reporting, and purchasing without scanning unrelated setup surfaces."
            : "This home screen is narrowed to your active work, current counts, and the next places you need to go."
        }
        serifTitle
        badges={[
          { label: isAdmin ? "Admin-first workspace" : "Role-aware workspace", tone: "brand" },
          { label: `${items.length} active announcements`, tone: "neutral" },
        ]}
      />

      <MetricStrip items={metricItems} />

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr),minmax(20rem,0.8fr)]">
        <SectionCard
          title={isAdmin ? "Where to move next" : "Quick access"}
          subtitle={
            isAdmin
              ? "Direct links into the operational surfaces that matter most today."
              : "Jump into your current work without scanning the full navigation tree."
          }
        >
          <div className="quick-link-list">
            {quickLinks.map((item) => (
              <Link key={item.to} to={item.to} className="quick-link-card">
                <div>
                  <div className="quick-link-title">{item.title}</div>
                  <div className="quick-link-copy">{item.copy}</div>
                </div>
                <span className="quick-link-arrow">→</span>
              </Link>
            ))}
          </div>
        </SectionCard>

        <SectionCard
          title={isAdmin ? "Queue health" : "Current status"}
          subtitle={
            isAdmin
              ? "Keep the highest-risk areas visible before moving into the live queue."
              : "A compact read on the work currently visible to you."
          }
        >
          <div className="summary-list">
            <div className="summary-row">
              <span>Pending calls</span>
              <strong>{stats.pendingCalls}</strong>
            </div>
            <div className="summary-row">
              <span>Overdue calls</span>
              <strong>{stats.overdueCalls}</strong>
            </div>
            <div className="summary-row">
              <span>Completed calls</span>
              <strong>{stats.completedCalls}</strong>
            </div>
            <div className="summary-row">
              <span>Orders in system</span>
              <strong>{stats.totalOrders}</strong>
            </div>
          </div>
        </SectionCard>
      </section>

      <SectionCard
        title="Recent announcements"
        subtitle="Internal notes and reminders remain visible here so operators do not have to hunt for context."
        actions={<span className="badge-pill tone-neutral">{items.length} active</span>}
      >
        {items.length === 0 ? (
          <EmptyState title="No announcements" message="Nothing active is posted right now." />
        ) : (
          <div className="space-y-4">
            {items.slice(0, 4).map((announcement) => (
              <article key={announcement.id} className="surface-muted p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-lg font-semibold tracking-tight text-slate-950">{announcement.title}</h3>
                  <span className="text-xs text-slate-500">
                    {new Date(announcement.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div
                  className="mt-3 text-sm leading-7 text-slate-600"
                  dangerouslySetInnerHTML={{ __html: announcement.content }}
                />
              </article>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
